import dayjs from 'dayjs'
import { useCallback } from 'react'
import { TableRow, TableCell, Chip, Box, Typography, Autocomplete, TextField } from '@mui/material'
import { useSnackbar } from 'notistack'
import {
  useMapInspectorContext,
  useMapInspectorContextDispatch,
  setSelectedCrop,
  setHoveredCrop,
} from '../context/map-inspector-context'
import { useMapCropsStyles } from './use-map-crops-styles'
import {
  getMaturingStart,
  getHarvestEnd,
  calculateOverlap,
  formatOverlapDuration,
  parseDurationDays,
} from './map-overlap-utils'
import { useOrgParams } from '~/common/use-org-params'
import { useOrgContext } from '~/common/context/organization/org-context'
import { useFarmChildren } from '~/crop/crop-modal/hooks/use-farm-children'
import { useUpdateCrop } from '~/api/supabase-mutations'
import { cropEdited, useCropsContextDispatch } from '~/common/context/crops/crops-context'

const fmtDate = (dateStr) => {
  if (!dateStr) return '—'
  const d = dayjs(dateStr)
  return d.isValid() ? d.format('MMM D') : '—'
}

// Dimension-markup timeline bar: |——— X weeks ———|
const OverlapBar = ({ text, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, px: 0.5 }}>
    <Box sx={{ width: 1.5, height: 10, bgcolor: color, flexShrink: 0 }} />
    <Box sx={{ flex: 1, height: 1, bgcolor: color, minWidth: 6 }} />
    <Typography component="span" sx={{ px: 1, whiteSpace: 'nowrap', fontSize: '0.7rem', color, fontWeight: 500 }}>
      {text}
    </Typography>
    <Box sx={{ flex: 1, height: 1, bgcolor: color, minWidth: 6 }} />
    <Box sx={{ width: 1.5, height: 10, bgcolor: color, flexShrink: 0 }} />
  </Box>
)

export const MapCropsRow = ({ crop, classifyCropDetailed, scheduleEntry, scheduleMap, successionMap }) => {
  const { selectedCropId, hoveredCropId, selectedCropOrgId } = useMapInspectorContext()
  const dispatch = useMapInspectorContextDispatch()
  const {
    HOVER_COLORS, SELECTED_COLORS, SELECTED_ROW_BORDER,
    RELATIONSHIP_COLUMN_WIDTH, relationshipMeta,
  } = useMapCropsStyles()

  // Org selector for crop reassignment
  const { farmId } = useOrgParams()
  const { activeOrgs } = useOrgContext()
  const { childOrgs } = useFarmChildren({ type: 'patch', farmId })
  const farmOrg = activeOrgs?.find(o => o.organization_type === 'farm')
  const orgOptions = farmOrg ? [farmOrg, ...childOrgs] : childOrgs
  const { updateCrop } = useUpdateCrop()
  const { enqueueSnackbar } = useSnackbar()
  const cropsDispatch = useCropsContextDispatch()

  const handleOrgChange = useCallback(async (_e, newOrg) => {
    if (!newOrg || newOrg.id === crop.organization_id) return
    const { error } = await updateCrop(crop.id, { organization_id: newOrg.id })
    if (error) {
      enqueueSnackbar(error.message, { variant: 'error' })
      return
    }
    enqueueSnackbar(`Crop moved to ${newOrg.name}`, { variant: 'success' })
    cropsDispatch(cropEdited({ cropId: crop.id }))
  }, [crop.id, crop.organization_id, updateCrop, enqueueSnackbar, cropsDispatch])

  const isThisSelected = selectedCropId === crop.id
  const isThisHovered = hoveredCropId === crop.id
  const activeCropId = selectedCropId || hoveredCropId

  const handleClick = () => {
    dispatch(setSelectedCrop({
      cropId: isThisSelected ? null : crop.id,
      orgId: isThisSelected ? null : crop.organization_id,
    }))
  }

  const phases = scheduleEntry?.phases || []
  const maturingPhase = phases.find(p => p.phase_type_code === 'maturing')
  const maturePhase = phases.find(p => p.phase_type_code === 'mature')

  const maturingDate = fmtDate(maturingPhase?.est_started_at)
  const durationDays = parseDurationDays(maturePhase?.duration)

  let harvestDisplay = '—'
  if (maturePhase?.est_started_at) {
    const firstHarvest = dayjs(maturePhase.est_started_at)
    const succession = successionMap?.get(crop.id)

    if (succession && succession.count > 0 && firstHarvest.isValid()) {
      let offset = 0
      for (const child of succession.children) {
        offset += child.interval_days || 0
      }
      const lastHarvestEnd = firstHarvest.add(offset + durationDays, 'day')
      harvestDisplay = `${firstHarvest.format('MMM D')} – ${lastHarvestEnd.format('MMM D')} (×${succession.count + 1})`
    } else if (durationDays > 0) {
      const harvestEnd = firstHarvest.add(durationDays, 'day')
      harvestDisplay = `${firstHarvest.format('MMM D')} – ${harvestEnd.format('MMM D')}`
    } else {
      harvestDisplay = fmtDate(maturePhase.est_started_at)
    }
  }

  const cropName = crop.name || crop.species_name || '—'

  // Compute background color and relationship type
  let rowBgcolor = 'transparent'
  let rel = 'neutral'

  if (isThisSelected) {
    rowBgcolor = 'rgba(0, 0, 0, 0.04)'
  } else if (activeCropId && activeCropId !== crop.id) {
    rel = classifyCropDetailed(activeCropId, crop.id)
    rowBgcolor = selectedCropId ? SELECTED_COLORS[rel] : HOVER_COLORS[rel]
  }

  const chipMeta = relationshipMeta[rel]

  // Same-org: left border on first cell (gapless across adjacent rows in collapsed table)
  const isSameOrg = !isThisSelected && selectedCropOrgId && crop.organization_id === selectedCropOrgId

  // Overlap: shown for related (non-neutral) crops when there is an active crop
  let overlapText = null
  const hasActiveRelationship = activeCropId && activeCropId !== crop.id
  if (
    rel !== 'neutral' &&
    hasActiveRelationship ||
    (hasActiveRelationship && isThisHovered)
  ) {
    const activeCropSchedule = scheduleMap?.get(activeCropId)
    const myStart = getMaturingStart(scheduleEntry)
    const myEnd = getHarvestEnd(scheduleEntry, successionMap, crop.id)
    const activeStart = getMaturingStart(activeCropSchedule)
    const activeEnd = getHarvestEnd(activeCropSchedule, successionMap, activeCropId)
    const overlap = calculateOverlap(myStart, myEnd, activeStart, activeEnd)
    overlapText = overlap ? formatOverlapDuration(overlap.days) : 'No overlap'
  }

  return (
    <TableRow
      hover
      sx={{
        bgcolor: rowBgcolor,
        cursor: 'pointer',
        transition: 'background-color 200ms ease-in-out',
        ...(isThisSelected && {
          outline: `2px solid ${SELECTED_ROW_BORDER}`,
          outlineOffset: '-1px',
        }),
      }}
      onMouseEnter={() => dispatch(setHoveredCrop({ cropId: crop.id }))}
      onMouseLeave={() => dispatch(setHoveredCrop({ cropId: null }))}
      onClick={handleClick}
    >
      {/* Crop name — left border carries the same-org indicator (gapless in collapsed table) */}
      <TableCell sx={{
        whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis',
        ...(isSameOrg && { borderLeft: '5px solid rgba(33, 150, 243, 0.65)' }),
      }}>
        {cropName}
      </TableCell>
      <TableCell onClick={e => e.stopPropagation()} sx={{ p: 0.5, minWidth: 110 }}>
        <Autocomplete
          size="small"
          options={orgOptions}
          value={orgOptions.find(o => o.id === crop.organization_id) ?? null}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={handleOrgChange}
          disableClearable
          blurOnSelect
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem' } }}
            />
          )}
        />
      </TableCell>

      {/* Maturing + arrow + Harvest: collapse into colSpan=3 overlap bar when overlap is active */}
      {overlapText ? (
        <TableCell colSpan={3} sx={{ py: 0.5 }}>
          <OverlapBar text={overlapText} color={chipMeta?.textColor || 'text.secondary'} />
        </TableCell>
      ) : (
        <>
          <TableCell
            sx={{
              whiteSpace: 'nowrap',
              fontSize: '0.75rem',
              px: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'right',
            }}
          >
            {maturingDate}
          </TableCell>
          <TableCell
            sx={{
              px: 0.5,
              width: 20,
              textAlign: 'center',
              color: 'text.disabled',
              fontSize: '0.75rem',
            }}
          >
            →
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', px: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {harvestDisplay}
          </TableCell>
        </>
      )}

      <TableCell sx={{ width: RELATIONSHIP_COLUMN_WIDTH, minWidth: RELATIONSHIP_COLUMN_WIDTH }}>
        {chipMeta && (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              size="small"
              icon={<chipMeta.Icon sx={{ fontSize: '0.65rem !important', color: `${chipMeta.solid ? '#fff' : chipMeta.textColor} !important` }} />}
              label={chipMeta.label}
              sx={{
                bgcolor: chipMeta.solid ? chipMeta.solidColor : rowBgcolor,
                color: chipMeta.solid ? '#fff' : chipMeta.textColor,
                border: chipMeta.solid ? 'none' : `1px solid ${chipMeta.textColor}`,
                fontSize: '0.65rem',
                height: 18,
                minHeight: 18,
                lineHeight: 1,
                p: 0,
                '.MuiChip-label': {
                  px: 0.75, py: 0,
                  lineHeight: 1.15,
                  display: 'flex',
                  alignItems: 'center',
                },
                whiteSpace: 'nowrap',
              }}
            />
          </Box>
        )}
      </TableCell>
    </TableRow>
  )
}
